import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { addDynamicColorClass, generateSafeClassName } from '../utils/dynamicStyles'

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

interface WildlifeAsset {
  id: number
  name: string
  type: string
  subcategory: string
  count: number
  description?: string
  site_name: string
  area_name?: string
  created_by_name: string
  comment?: string
  time_started: string
  position_type: 'point' | 'polygon'
  position_coords: [number, number]
  duration: number
  status: 'active' | 'completed'
  observation_type?: 'observation' | 'wounded' | 'downed' | 'tracks'
  group_female?: number | boolean
  group_young?: number | boolean
  group_male?: number | boolean
}

interface WildlifeType {
  name: string
  icon: string
  color: string
}

interface WildlifeViewProps {
  onClose: () => void
}

// Create custom wildlife markers
const createWildlifeIcon = (subcategory: string, wildlifeTypes: Record<string, WildlifeType>, isRecent: boolean = false) => {
  const typeInfo = wildlifeTypes[subcategory] || { icon: '🦌', color: '#228B22' }
  const recentClass = isRecent ? 'marker-recent' : ''
  
  // Add dynamic color class to document head
  const colorClass = generateSafeClassName('wildlife-color', subcategory);
  addDynamicColorClass(colorClass, typeInfo.color);
  
  return new L.DivIcon({
    html: `
      <div class="custom-marker-base ${recentClass} ${colorClass}">
        <span class="marker-icon">${typeInfo.icon}</span>
        ${isRecent ? '<div class="marker-badge">!</div>' : ''}
      </div>
    `,
    className: 'wildlife-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  })
}

function WildlifeView({ onClose }: WildlifeViewProps) {
  const [wildlifeAssets, setWildlifeAssets] = useState<WildlifeAsset[]>([])
  const [wildlifeTypes, setWildlifeTypes] = useState<Record<string, WildlifeType>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<'list' | 'map' | 'recent'>('list')
  const [timeFilter, setTimeFilter] = useState<'all' | '24h' | '7d'>('all')
  const [obsTypeFilter, setObsTypeFilter] = useState<'all' | 'observation' | 'wounded' | 'downed' | 'tracks'>('all')

  useEffect(() => {
    fetchWildlifeAssets()
    fetchWildlifeTypes()
  }, [timeFilter])

  const fetchWildlifeAssets = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/assets?type=wild_animal')
      if (response.ok) {
        const data = await response.json()
        setWildlifeAssets(data.assets || [])
      } else {
        throw new Error('Failed to fetch wildlife assets')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load wildlife data')
    } finally {
      setLoading(false)
    }
  }

  const fetchWildlifeTypes = async () => {
    try {
      const response = await fetchWithAuth('/api/assets/wildlife-types')
      if (response.ok) {
        const data = await response.json()
        setWildlifeTypes(data)
      }
    } catch (err) {
      console.error('Failed to fetch wildlife types:', err)
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toISOString().slice(0, 10)
  }

  const getFilteredAssets = () => {
    const now = new Date()
    return wildlifeAssets.filter(asset => {
      const assetDate = new Date(asset.time_started)
      const diffHours = (now.getTime() - assetDate.getTime()) / (1000 * 60 * 60)
      
      switch (timeFilter) {
        case '24h': return diffHours <= 24
        case '7d': return diffHours <= 168 // 7 * 24
        default: return true
      }
    }).filter(asset => {
      if (obsTypeFilter === 'all') return true
      return (asset.observation_type || 'observation') === obsTypeFilter
    })
  }

  const getRecentAssets = () => {
    const now = new Date()
    return wildlifeAssets.filter(asset => {
      const assetDate = new Date(asset.time_started)
      const diffHours = (now.getTime() - assetDate.getTime()) / (1000 * 60 * 60)
      return diffHours <= 24
    }).filter(asset => {
      if (obsTypeFilter === 'all') return true
      return (asset.observation_type || 'observation') === obsTypeFilter
    })
  }

  const calculateMapCenter = (assets: WildlifeAsset[]): [number, number] => {
    if (assets.length === 0) return [59.3293, 18.0686]
    
    let totalLat = 0, totalLng = 0
    assets.forEach(asset => {
      totalLat += asset.position_coords[1]
      totalLng += asset.position_coords[0]
    })
    
    return [totalLat / assets.length, totalLng / assets.length]
  }

  const filteredAssets = getFilteredAssets()
  const recentAssets = getRecentAssets()

  if (loading) {
    return (
      <div className="wildlife-view-overlay">
        <div className="wildlife-view-container">
          <div className="loading">Loading wildlife data...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="wildlife-view-overlay">
        <div className="wildlife-view-container">
          <div className="error">{error}</div>
          <button className="btn" onClick={fetchWildlifeAssets}>Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="wildlife-view-overlay">
      <div className="wildlife-view-container">
        <div className="wildlife-view-header">
          <h2>🦌 Wildlife Observations</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="wildlife-view-nav">
          <div className="view-tabs">
            <button 
              className={`tab-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >
              📋 List ({filteredAssets.length})
            </button>
            <button 
              className={`tab-btn ${view === 'map' ? 'active' : ''}`}
              onClick={() => setView('map')}
            >
              🗺️ Show on Map
            </button>
            <button 
              className={`tab-btn ${view === 'recent' ? 'active' : ''}`}
              onClick={() => setView('recent')}
            >
              ⚡ Last 24h ({recentAssets.length})
            </button>
          </div>

          <div className="time-filters">
            <select
              aria-label="Observation time filter"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as 'all' | '24h' | '7d')}
              style={{ marginRight: '0.5rem' }}
            >
              <option value="all">All Time</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
            <select
              aria-label="Observation type filter"
              value={obsTypeFilter}
              onChange={(e) => setObsTypeFilter(e.target.value as any)}
            >
              <option value="all">All Types</option>
              <option value="observation">observation</option>
              <option value="wounded">Wounded game</option>
              <option value="downed">Downed Game</option>
              <option value="tracks">Tracks</option>
            </select>
          </div>
        </div>

        <div className="wildlife-view-content">
          {view === 'list' && (
            <div className="wildlife-list">
              {filteredAssets.length === 0 ? (
                <div className="no-wildlife">
                  <p>No wildlife observations found</p>
                  <p>Use "🦌 Quick Wildlife Observation" to add sightings</p>
                </div>
              ) : (
                <div className="wildlife-grid">
                  {filteredAssets.map(asset => {
                    const typeInfo = wildlifeTypes[asset.subcategory] || { icon: '🦌', color: '#228B22' }
                    const isRecent = (new Date().getTime() - new Date(asset.time_started).getTime()) / (1000 * 60 * 60) <= 24
                    
                    return (
                      <div key={asset.id} className="wildlife-card">
                        <div 
                          className="wildlife-card-header"
                          style={{ backgroundColor: typeInfo.color }}
                        >
                          <div className="wildlife-icon">{typeInfo.icon}</div>
                          <div className="wildlife-title">
                            <h3>{asset.name}</h3>
                            <span className="wildlife-time">
                              {formatTimeAgo(asset.time_started)}
                              {isRecent && <span className="recent-badge">🔥</span>}
                            </span>
                          </div>
                        </div>
                        <div className="wildlife-card-body">
                          <div className="wildlife-location">
                            🏠 {asset.site_name}
                            {asset.area_name && <><br />📍 {asset.area_name}</>}
                          </div>
                          <div className="wildlife-meta">
                            {(() => {
                              const t = (asset.observation_type || 'observation') as 'observation'|'wounded'|'downed'|'tracks'
                              const labelMap: Record<string,string> = { observation: 'observation', wounded: 'Wounded game', downed: 'Downed Game', tracks: 'Tracks' }
                              const typeClass = `badge-type-${t}`
                              return (
                                <div className="meta-row">
                                  <span className={`badge badge-type ${typeClass}`}>{labelMap[t] || t}</span>
                                </div>
                              )
                            })()}
                            {((asset.group_female ? 1 : 0) + (asset.group_young ? 1 : 0) + (asset.group_male ? 1 : 0) > 0) && (
                              <div className="meta-row">
                                <span className="badge badge-group">Group</span>
                                {asset.group_female ? <span className="chip chip-female">Female</span> : null}
                                {asset.group_young ? <span className="chip chip-young">Young</span> : null}
                                {asset.group_male ? <span className="chip chip-male">Male</span> : null}
                              </div>
                            )}
                          </div>
                          {asset.comment && (
                            <div className="wildlife-comment">💭 {asset.comment}</div>
                          )}
                          <div className="wildlife-meta">
                            <span>👤 {asset.created_by_name}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {(view === 'map' || view === 'recent') && (
            <div className="wildlife-map-section">
              <div className="map-header">
                <h3>
                  {view === 'recent' ? '⚡ Recent Wildlife Activity (24h)' : '🗺️ All Wildlife Locations'}
                </h3>
                <div className="map-stats">
                  {view === 'recent' ? (
                    <span>{recentAssets.length} observations in last 24 hours</span>
                  ) : (
                    <span>{filteredAssets.length} total observations</span>
                  )}
                </div>
              </div>
              
              {(view === 'recent' ? recentAssets : filteredAssets).length === 0 ? (
                <div className="no-wildlife">
                  <p>No {view === 'recent' ? 'recent' : ''} wildlife observations to display</p>
                </div>
              ) : (
                <MapContainer
                  center={calculateMapCenter(view === 'recent' ? recentAssets : filteredAssets)}
                  zoom={12}
                  style={{ height: '500px', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {(view === 'recent' ? recentAssets : filteredAssets).map(asset => {
                    const isRecent = (new Date().getTime() - new Date(asset.time_started).getTime()) / (1000 * 60 * 60) <= 24
                    
                    return (
                      <Marker
                        key={asset.id}
                        position={[asset.position_coords[1], asset.position_coords[0]]}
                        icon={createWildlifeIcon(asset.subcategory, wildlifeTypes, view === 'recent' || isRecent)}
                      >
                        <Popup>
                          <div className="wildlife-popup">
                            <h4>{asset.name}</h4>
                            <div className="popup-time">
                              {formatTimeAgo(asset.time_started)}
                              {isRecent && <span className="recent-badge">🔥</span>}
                            </div>
                            <div className="popup-location">
                              🏠 {asset.site_name}
                              {asset.area_name && <><br />📍 {asset.area_name}</>}
                            </div>
                            {(() => {
                              const t = (asset.observation_type || 'observation') as 'observation'|'wounded'|'downed'|'tracks'
                              const labelMap: Record<string,string> = { observation: 'observation', wounded: 'Wounded game', downed: 'Downed Game', tracks: 'Tracks' }
                              const typeClass = `badge-type-${t}`
                              return (
                                <div className="popup-badges">
                                  <span className={`badge badge-type ${typeClass}`}>{labelMap[t] || t}</span>
                                  {((asset.group_female ? 1 : 0) + (asset.group_young ? 1 : 0) + (asset.group_male ? 1 : 0) > 0) && (
                                    <span className="badge badge-group-inline">
                                      Group:
                                      {asset.group_female ? <span className="chip chip-female">Female</span> : null}
                                      {asset.group_young ? <span className="chip chip-young">Young</span> : null}
                                      {asset.group_male ? <span className="chip chip-male">Male</span> : null}
                                    </span>
                                  )}
                                </div>
                              )
                            })()}
                            {asset.comment && (
                              <div className="popup-comment">💭 {asset.comment}</div>
                            )}
                            <div className="popup-meta">
                              👤 {asset.created_by_name}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    )
                  })}
                </MapContainer>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WildlifeView
